import {EditorState, RawDraftContentState} from 'draft-js';
import {draftToMarkdown, markdownToDraft} from 'markdown-draft-js';
import {COMMENT_MENTION_LINK_PREFIX} from '@ui/atoms/typography/comment-mention';

export const normalizeRawContent = (
  value: unknown,
): RawDraftContentState | null => {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as {
    blocks?: unknown;
    entityMap?: unknown;
    entities?: unknown;
  };

  if (candidate.entities) return normalizeRawContent(candidate.entities);
  if (!Array.isArray(candidate.blocks)) return null;

  return {
    blocks: candidate.blocks as RawDraftContentState['blocks'],
    entityMap:
      candidate.entityMap && typeof candidate.entityMap === 'object'
        ? (candidate.entityMap as RawDraftContentState['entityMap'])
        : {},
  };
};

export const shouldHideRichTextPlaceholder = (editorState: EditorState) => {
  const content = editorState.getCurrentContent();

  return (
    !content.hasText() && content.getFirstBlock().getType() !== 'unstyled'
  );
};

export const rawContentToMarkdown = (value: unknown) => {
  const raw = normalizeRawContent(value);
  if (!raw) return '';

  try {
    return draftToMarkdown(raw, {
      entityItems: {
        mention: {
          open: () => '[',
          close: (entity) => {
            const mentionId = String(
              ((entity as unknown) as {
                data?: {mention?: {id?: string}};
              })?.data?.mention?.id || '',
            );

            return `](${COMMENT_MENTION_LINK_PREFIX}${encodeURIComponent(
              mentionId,
            )})`;
          },
        },
      },
    }).trim();
  } catch {
    return '';
  }
};

export const rawContentToPlainText = (value: unknown) => {
  const raw = normalizeRawContent(value);
  if (!raw) return '';

  return raw.blocks
    .map((block) => block.text)
    .join('\n')
    .trim();
};

export const markdownToRawContent = (value?: string | null) => {
  try {
    return normalizeRawContent(markdownToDraft(value || ''));
  } catch {
    return normalizeRawContent(markdownToDraft(''));
  }
};

export const markdownToPlainText = (value?: string | null) => {
  const raw = markdownToRawContent(value);
  return rawContentToPlainText(raw);
};

export const serializeCommentText = (
  value: unknown,
  mentions: {id: string; name: string}[],
) => {
  let text = rawContentToPlainText(value);
  mentions.forEach((mention) => {
    text = text.split(`@${mention.name}`).join(`_(${mention.id})_`);
  });
  return text;
};
